import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ROUTES } from '../constants/routes';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  }, []);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 403 && err.response?.data?.error === 'PASSWORD_CHANGE_REQUIRED') {
          if (!window.location.pathname.startsWith(ROUTES.CHANGE_PASSWORD)) {
            window.location.replace(`${window.location.origin}${ROUTES.CHANGE_PASSWORD}`);
          }
          return Promise.reject(err);
        }
        if (err.response?.status !== 401) return Promise.reject(err);
        const auth = err.config?.headers?.Authorization;
        if (!auth || !String(auth).startsWith('Bearer')) {
          return Promise.reject(err);
        }
        logoutRef.current();
        if (!window.location.pathname.startsWith(ROUTES.LOGIN)) {
          window.location.replace(`${window.location.origin}${ROUTES.LOGIN}?reason=session`);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!ignore) setUser(res.data);
      } catch (_err) {
        if (!ignore) logoutRef.current();
      }
    })();
    return () => {
      ignore = true;
    };
  }, [token]);

  useEffect(() => {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else localStorage.removeItem('user');
  }, [user]);

  useEffect(() => {
    if (token) localStorage.setItem('token', token);
    else localStorage.removeItem('token');
  }, [token]);

  const login = (user, token) => {
    setUser(user);
    setToken(token);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
} 